import React from "react";
import { Frown, Smile } from "lucide-react";
import EntityAvatar from "components/Entities/EntityAvatar";
import AlignmentSummaryDisplay from "components/Alignment/AlignmentSummaryDisplay";
import DetailPanel from "ui/DetailPanel";
import { useUser } from "util/UserContext";
import type { ViewerRelation } from "util/constants";
import type { QuizResultParticipant, QuizTeabateSelection, QuizTeabateStance } from "types/quiz";
import type { QuizUiStrings } from "i18n/quizUi";
import QuizAlignmentTile from "./QuizAlignmentTile";
import { mapQuizUserToEntityAvatarUser } from "./quizUtils";

interface QuizResultsPlayerProps {
  ui: QuizUiStrings;
  participant: QuizResultParticipant;
  onTeabateSelect: (teabateId: number) => void;
  quizOnly: boolean;
  statementFilterIds: number[];
  allQuizTeabates: QuizTeabateSelection[];
  viewerStances: QuizTeabateStance[];
}

/**
 * Per-player results card shown inside the results carousel.
 */
const QuizResultsPlayer: React.FC<QuizResultsPlayerProps> = ({
  ui,
  participant,
  onTeabateSelect,
  quizOnly,
  statementFilterIds,
  allQuizTeabates,
  viewerStances,
}) => {
  const { currentUser } = useUser();
  const hasAlignmentData = currentUser !== null && (!quizOnly || statementFilterIds.length > 0);
  const topRanking = participant.issue_rankings[0] || null;

  const getRankClasses = (sentiment: number | null): string => {
    const base = "flex-shrink-0 w-6 pb-0.5 pt-3 rounded-full text-white text-xs font-semibold flex flex-col items-center justify-between gap-0 leading-none";
    if (sentiment === 1) {
      return `${base} bg-green-600`;
    }
    if (sentiment === -1) {
      return `${base} bg-red-600`;
    }
    return `${base} bg-eoe-gray dark:bg-eoe-gray`;
  };

  const renderRankingRow = (ranking: QuizResultParticipant["issue_rankings"][number]): React.ReactElement => (
    <div
      key={`${participant.user_id}-${ranking.issue_id}`}
      className="flex gap-1 p-1 bg-eoe-bg-hover dark:bg-eoe-dark-bg-hover rounded-xl"
    >
      <div className={getRankClasses(ranking.sentiment)}>
        <span>#{ranking.rank}</span>
        {ranking.sentiment === 1 ? <Smile className="size-4" /> : null}
        {ranking.sentiment === -1 ? <Frown className="size-4" /> : null}
      </div>
      <div className="flex-grow min-w-0">
        <EntityAvatar
          entityType="issue"
          entityId={ranking.issue.id ?? ranking.issue_id ?? undefined}
          entityChip={true}
          showCloseChip={false}
          showLink={false}
          className="flex-grow min-w-0"
        />
      </div>
    </div>
  );

  const responseEntries = participant.stances
    .map((stance) => {
      const teabate = allQuizTeabates.find((entry) => entry.id === stance.quiz_teabate_id) || null;
      if (!teabate) {
        return null;
      }

      return {
        teabate,
        targetStance: stance,
        currentViewerStance: viewerStances.find((viewerStance) => viewerStance.quiz_teabate_id === stance.quiz_teabate_id) || null,
      };
    })
    .filter((entry): entry is {
      teabate: QuizTeabateSelection;
      targetStance: QuizTeabateStance;
      currentViewerStance: QuizTeabateStance | null;
    } => entry !== null);

  return (
    <div className="space-y-5 rounded-2xl border border-eoe-border-main bg-eoe-bg-modal p-5 dark:border-eoe-dark-border-main dark:bg-eoe-dark-bg-modal">
      <EntityAvatar
        entity={mapQuizUserToEntityAvatarUser(participant.user)}
        entityType="user"
        entityId={participant.user_id}
        entityChip={true}
        showLink={true}
        isInputField={false}
        className="min-w-0"
      />

      <div className="grid gap-4 lg:grid-cols-[0.9fr_1.1fr]">
        <section className="space-y-4">
          <DetailPanel
            title={ui.topIssuesTitle}
            showTopCollapse={false}
            showBottomCollapse={true}
            className="bg-eoe-bg-modal dark:bg-eoe-dark-bg-modal"
            headingContent={
              topRanking ? renderRankingRow(topRanking) : undefined
            }
          >
            <div className="space-y-2">
              {participant.issue_rankings.slice(1).map((ranking) => renderRankingRow(ranking))}
            </div>
          </DetailPanel>

          {responseEntries.length > 0 ? (
            <DetailPanel
              title={ui.responsesTitle}
              showTopCollapse={false}
              showBottomCollapse={true}
              className="bg-eoe-bg-modal dark:bg-eoe-dark-bg-modal"
            >
              <div className="space-y-3">
                {responseEntries.map((entry) => (
                  <QuizAlignmentTile
                    key={entry.targetStance.id ?? `${participant.user_id}-${entry.teabate.id}`}
                    ui={ui}
                    teabate={entry.teabate}
                    targetStance={entry.targetStance}
                    currentViewerStance={entry.currentViewerStance}
                  />
                ))}
              </div>
            </DetailPanel>
          ) : null}
        </section>

        <section>
          <DetailPanel
            title={ui.overallAlignmentTitle}
            showTopCollapse={false}
            showBottomCollapse={true}
            className="bg-eoe-bg-modal dark:bg-eoe-dark-bg-modal"
          >
            {hasAlignmentData ? (
              <AlignmentSummaryDisplay
                targetUsers={[{ id: participant.user_id }]}
                currentUser={currentUser}
                targetLabel={ui.thisPlayerLabel}
                currentUserViewerRelation={"public" as ViewerRelation}
                onStatementSelect={onTeabateSelect}
                statementFilterIds={quizOnly ? statementFilterIds : null}
              />
            ) : (
              <p className="text-sm text-eoe-text-sub dark:text-eoe-dark-text-sub">{ui.noAlignmentData}</p>
            )}
          </DetailPanel>
        </section>
      </div>
    </div>
  );
};

export default QuizResultsPlayer;
